import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/client";
import toast from "react-hot-toast";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import type { AxiosError } from "axios";

interface ApiErrorResponse {
  message?: string;
}

interface LandingPageSections {
  hero: boolean;
  trustIndicators: boolean;
  calculator: boolean;
  howItWorks: boolean;
  features: boolean;
  comparison: boolean;
  whyClaroclaim: boolean;
  faqVideos: boolean;
  faq: boolean;
  cta: boolean;
}

interface LandingPageHeroOverrides {
  mainTitle?: string;
  subtitle?: string;
  ctaText?: string;
  accentColor?: string;
}

interface SectionHeadline {
  title?: {
    de?: string;
    en?: string;
  };
  subtitle?: {
    de?: string;
    en?: string;
  };
}

interface LandingPageSectionHeadlines {
  hero?: SectionHeadline;
  trustIndicators?: SectionHeadline;
  calculator?: SectionHeadline;
  howItWorks?: SectionHeadline;
  features?: SectionHeadline;
  comparison?: SectionHeadline;
  whyClaroclaim?: SectionHeadline;
  faqVideos?: SectionHeadline;
  faq?: SectionHeadline;
  cta?: SectionHeadline;
}

interface LandingPageConfig {
  _id?: string;
  variant: string;
  slug: string;
  isActive: boolean;
  sections: LandingPageSections;
  heroOverrides?: LandingPageHeroOverrides;
  sectionHeadlines?: LandingPageSectionHeadlines;
  createdAt?: Date;
  updatedAt?: Date;
}

const variantMeta: Record<string, { name: string; icon: string; color: string; gradient: string }> = {
  default: { name: "Default", icon: "🏠", color: "bg-gray-500", gradient: "from-gray-100 to-white" },
  facebook: { name: "Facebook", icon: "📘", color: "bg-blue-600", gradient: "from-blue-50 to-white" },
  instagram: { name: "Instagram", icon: "📸", color: "bg-pink-500", gradient: "from-pink-50 via-purple-50 to-white" },
  x: { name: "X / Twitter", icon: "𝕏", color: "bg-gray-900", gradient: "from-gray-900 to-gray-800" },
  linkedin: { name: "LinkedIn", icon: "💼", color: "bg-sky-600", gradient: "from-sky-50 to-white" },
  reddit: { name: "Reddit", icon: "🔶", color: "bg-orange-500", gradient: "from-orange-50 to-white" },
  tiktok: { name: "TikTok", icon: "🎵", color: "bg-black", gradient: "from-pink-50 to-cyan-50" },
};

const sectionLabels: Record<keyof LandingPageSections, string> = {
  hero: "Hero Section",
  trustIndicators: "Trust Indicators",
  calculator: "Calculator",
  howItWorks: "How It Works",
  features: "Features",
  comparison: "Comparison",
  whyClaroclaim: "Why Claroclaim",
  faqVideos: "FAQ Videos",
  faq: "FAQ",
  cta: "CTA Section",
};

export default function LandingPages() {
  const queryClient = useQueryClient();
    const [localConfigs, setLocalConfigs] = useState<Record<string, LandingPageConfig>>({});

  const { data: configs, isLoading, error } = useQuery({
    queryKey: ["landing-pages"],
    queryFn: async () => {
      const res = await api.get("/admin/landing-pages");
      return res.data.data as LandingPageConfig[];
    },
  });

  const initMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post("/admin/landing-pages/init");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages"] });
      toast.success("Landing pages initialized");
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(error.response?.data?.message || "Failed to initialize");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (config: LandingPageConfig) => {
      const slugParam = config.slug || config.variant;
      const res = await api.put(`/admin/landing-pages/${slugParam}`, config);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-pages"] });
      toast.success("Configuration saved");
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      toast.error(error.response?.data?.message || "Failed to save");
    },
  });

  // Initialize local state when data loads
  useEffect(() => {
    if (configs) {
      const configMap: Record<string, LandingPageConfig> = {};
      configs.forEach((c) => {
        const slugFallbacks: Record<string, string> = {
          default: "default",
          facebook: "fb",
          instagram: "ig",
          x: "x",
          linkedin: "li",
          reddit: "rd",
          tiktok: "tt",
        };
        const normalizedSlug =
          c.slug || slugFallbacks[c.variant] || c.variant || "default";
        configMap[normalizedSlug] = { ...c, slug: normalizedSlug };
      });
      setLocalConfigs(configMap);
    }
  }, [configs]);

  const handleToggleSection = (slug: string, section: keyof LandingPageSections) => {
    setLocalConfigs((prev) => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        sections: {
          ...prev[slug].sections,
          [section]: !prev[slug].sections[section],
        },
      },
    }));
  };

  const handleToggleActive = (slug: string) => {
    setLocalConfigs((prev) => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        isActive: !prev[slug].isActive,
      },
    }));
  };

  const handleSave = (slug: string) => {
    const config = localConfigs[slug];
    if (config) {
      updateMutation.mutate(config);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading landing pages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error loading landing pages</div>
      </div>
    );
  }

  // Show init button if no configs exist
  if (!configs || configs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <GlobeAltIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Landing Pages</h1>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
          <GlobeAltIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Landing Page Configurations
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Initialize default landing page configurations for all channels.
          </p>
          <button
            onClick={() => initMutation.mutate()}
            disabled={initMutation.isPending}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {initMutation.isPending ? "Initializing..." : "Initialize Landing Pages"}
          </button>
        </div>
      </div>
    );
  }

  const sortedConfigs = Object.values(localConfigs).sort((a, b) => {
    const order = ["default", "facebook", "instagram", "x", "linkedin", "reddit", "tiktok"];
    return order.indexOf(a.variant) - order.indexOf(b.variant);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <GlobeAltIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Landing Pages</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Configure channel-specific landing page variants
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedConfigs.map((config) => {
          const meta = variantMeta[config.variant] || variantMeta.default;

          return (
            <div
              key={config.slug}
              className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
            >
              {/* Card Header */}
              <div className={`p-4 bg-gradient-to-r ${meta.gradient} dark:from-gray-700 dark:to-gray-800`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{meta.icon}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {meta.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        /{config.slug === "default" ? "" : `c/${config.slug}`}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.isActive}
                      onChange={() => handleToggleActive(config.slug)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>

              {/* Section Toggles */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(sectionLabels) as Array<keyof LandingPageSections>).map((section) => (
                    <label
                      key={section}
                      className="flex items-center space-x-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={config.sections[section]}
                        onChange={() => handleToggleSection(config.slug, section)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-700 dark:text-gray-300 truncate">
                        {sectionLabels[section]}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Save Button */}
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => handleSave(config.slug)}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              URL Structure
            </h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <p>Channel landing pages are accessible at:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><code>/:locale/</code> - Default landing page</li>
                <li><code>/:locale/c/fb</code> - Facebook variant</li>
                <li><code>/:locale/c/ig</code> - Instagram variant</li>
                <li><code>/:locale/c/x</code> - X/Twitter variant</li>
                <li><code>/:locale/c/li</code> - LinkedIn variant</li>
                <li><code>/:locale/c/rd</code> - Reddit variant</li>
                <li><code>/:locale/c/tt</code> - TikTok variant</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
